import { ImportMappingDto } from "../../../../domain/DataKitDto";

export const MAPPING_TARGETS = [
  { value: "IGNORE", label: "Don't import" },
  { value: "NAME", label: "Item Name" },
  { value: "GROUP", label: "Group" },
  { value: "SUBGROUP", label: "Subgroup" },
  { value: "DESCRIPTION", label: "Description" },
  { value: "EXTRAS", label: "Extras" },
  { value: "SIZE", label: "Size" },
  { value: "PRICE", label: "Price" },
  { value: "MODIFIER_GROUP", label: "Modifier group" },
  { value: "MODIFIERS", label: "Modifiers" },
  { value: "TAGS", label: "Tags" },
  // Named as in Timber's importer. Repeatable: every matched column adds its own header as a tag
  // on the items marked yes in it.
  { value: "DIETARY", label: "Dietary / Allergen" },
  { value: "EXTERNAL_ID", label: "External ID" },
];

/**
 * The match step's sections, in the order Timber's own importer shows them: Required, Optional,
 * Prices, Dietary / Allergen. Same targets as [MAPPING_TARGETS] - this only groups them, so
 * nothing can drift between the two. A column is listed under the section of whatever it is
 * currently matched to; anything left as "Don't import" is listed after the four.
 */
export const MAPPING_SECTIONS = [
  { key: "required", label: "Required", targets: ["GROUP", "NAME"] },
  {
    key: "optional",
    label: "Optional",
    targets: ["SUBGROUP", "DESCRIPTION", "EXTRAS", "TAGS", "MODIFIER_GROUP", "MODIFIERS", "EXTERNAL_ID"],
  },
  { key: "prices", label: "Prices — Size is optional per pair", targets: ["SIZE", "PRICE"] },
  { key: "dietary", label: "Dietary / Allergen", targets: ["DIETARY"] },
].map((section) => ({
  key: section.key,
  label: section.label,
  targets: section.targets
    .map((value) => MAPPING_TARGETS.find((t) => t.value === value))
    .filter((t): t is { value: string; label: string } => !!t),
}));

/** Which match-step section a target is listed under; "ignore" for Don't import. */
export const sectionOfTarget = (target: string) =>
  MAPPING_SECTIONS.find((section) => section.targets.some((t) => t.value === target))?.key || "ignore";

export const labelForTarget = (target: string) =>
  MAPPING_TARGETS.find((t) => t.value === target)?.label || target;

const EXACTLY_ONCE = ["GROUP", "NAME"];
const AT_MOST_ONCE = ["DESCRIPTION", "EXTRAS", "EXTERNAL_ID"];

/**
 * Targets any number of columns can share. These never show "already mapped" - that label is only
 * for fields a file has once (Item Name, Group...) and for numbered slots already taken.
 */
export const REPEATABLE = ["IGNORE", "DIETARY", "TAGS"];
// Repeatable, numbered targets. SIZE/PRICE pair a size label with its price; MODIFIER_GROUP/
// MODIFIERS pair a modifier group's name with its delimited modifier list. Same pairIndex
// machinery on the server for both.
// Subgroup is numbered too: groups nest, so slot 1 sits under the Group column, slot 2 under
// slot 1, and so on for as deep as the file goes.
const PAIRED = ["SIZE", "PRICE", "MODIFIER_GROUP", "MODIFIERS", "SUBGROUP"];

const labelFor = (target: string) =>
  MAPPING_TARGETS.find((t) => t.value === target)?.label || target;

export function needsPairIndex(target: string): boolean {
  return PAIRED.includes(target);
}

/** "Size 2", "Price 1" - how a numbered slot reads everywhere in the importer. */
export const slotLabel = (target: string, pairIndex: number) => `${labelForTarget(target)} ${pairIndex}`;

/**
 * The match dropdown's value for a mapping: the target itself, or `TARGET:n` for a numbered slot,
 * so Size 1 and Size 2 are separate options rather than a target plus a second slot picker.
 */
export const optionValueOf = (target: string, pairIndex?: number | null) =>
  needsPairIndex(target) ? `${target}:${pairIndex ?? FIRST_PAIR}` : target;

export function parseOptionValue(value: string): { target: string; pairIndex: number | null } {
  const [target, pair] = value.split(":");
  return { target, pairIndex: needsPairIndex(target) ? Number(pair) || FIRST_PAIR : null };
}

/**
 * Pair numbers are 1-based, matching the server.
 *
 * They used to be 0-based here while `DataKitImportNormaliseService.resolveTarget` rejects
 * anything below 1 outright, so any column the member mapped by hand to Size/Price came back a
 * 400 - the suffixed form the parse step suggests (`PRICE1`) was the only shape that ever
 * reached the server intact, which is what hid it.
 */
export const FIRST_PAIR = 1;

const PAIR_SUFFIX = /^(SIZE|PRICE|MODIFIER_GROUP|MODIFIERS|SUBGROUP)([1-9]\d*)$/;

/**
 * Splits a suggested target into the form this UI works in.
 *
 * The parse step suggests targets in the server's suffixed form - `SIZE1`, `MODIFIER_GROUP2` -
 * which is a legal wire value but not one of [MAPPING_TARGETS], so feeding it straight into the
 * dropdown showed the member a raw internal name with no matching option and no pair selector.
 * Split here instead, once, at the point the suggestion is turned into editable state.
 */
export function splitSuggestedTarget(raw: string): { target: string; pairIndex: number | null } {
  const value = (raw || "").trim().toUpperCase();
  if (!value) return { target: "IGNORE", pairIndex: null };

  const matched = PAIR_SUFFIX.exec(value);
  if (matched) return { target: matched[1], pairIndex: Number(matched[2]) };

  return { target: value, pairIndex: needsPairIndex(value) ? FIRST_PAIR : null };
}

export function validateMapping(mapping: ImportMappingDto[]): string[] {
  const errors: string[] = [];
  const countOf = (target: string) => mapping.filter((m) => m.target === target).length;

  EXACTLY_ONCE.forEach((target) => {
    const count = countOf(target);
    if (count === 0) errors.push(`Map one column to ${labelFor(target)}.`);
    if (count > 1) errors.push(`Only one column can be ${labelFor(target)}.`);
  });

  AT_MOST_ONCE.forEach((target) => {
    if (countOf(target) > 1) errors.push(`Only one column can be ${labelFor(target)}.`);
  });

  PAIRED.forEach((target) => {
    const seen = new Set<number>();
    mapping
      .filter((m) => m.target === target)
      .forEach((m) => {
        const pair = m.pairIndex ?? FIRST_PAIR;
        if (seen.has(pair)) {
          errors.push(`Two columns are matched to ${slotLabel(target, pair)}.`);
        }
        seen.add(pair);
      });
  });

  const pricePairs = new Set(
    mapping.filter((m) => m.target === "PRICE").map((m) => m.pairIndex ?? FIRST_PAIR)
  );
  mapping
    .filter((m) => m.target === "SIZE")
    .forEach((m) => {
      const pair = m.pairIndex ?? FIRST_PAIR;
      if (!pricePairs.has(pair)) {
        errors.push(`${slotLabel("SIZE", pair)} has no matching ${slotLabel("PRICE", pair)} column.`);
      }
    });

  // The modifier pair's dependency runs the other way to size/price. A group name with no modifier
  // column is fine - "Choice of side" on its own is a real, if empty, group - but a list of
  // modifiers with no group name has nowhere to go, and the server drops the whole pair.
  const modifierGroupPairs = new Set(
    mapping.filter((m) => m.target === "MODIFIER_GROUP").map((m) => m.pairIndex ?? FIRST_PAIR)
  );
  mapping
    .filter((m) => m.target === "MODIFIERS")
    .forEach((m) => {
      const pair = m.pairIndex ?? FIRST_PAIR;
      if (!modifierGroupPairs.has(pair)) {
        errors.push(`Modifiers ${pair} has no matching modifier group name column.`);
      }
    });

  return Array.from(new Set(errors));
}
